<template>
    <div class="season-to-date-chart">
        <h2 v-if="upToDate">Broadway Then and Now</h2>
        <h2 v-else>Season to Date</h2>

        <ul class="chart-view-tabs">
            <li
                v-for="(option, index) in dataViews"
                :key="option.label"
                :class="{ current: index === currentViewIndex }"
                @click.prevent="currentViewIndex = index"
            >
                {{ option.label }}
            </li>
        </ul>

        <line-chart
            v-if="chartData.length > 0"
            :display-x-grid="false"
            :chart-data="chartData"
            :animate="true"
            :plugins="plugins"
            :annotation="annotation"
        />
    </div>
</template>

<script>
import api from 'src/api';
import range from 'lodash/range';
import findLastKey from 'lodash/findLastKey';
import subDays from 'date-fns/sub_days';
import addDays from 'date-fns/add_days';
import { shortenNumber, formatDate } from 'src/util/helpers';
import findIndex from 'lodash/findIndex';
import isEqual from 'date-fns/is_equal';
import keyBy from 'lodash/keyBy';
import differenceInCalendarWeeks from 'date-fns/difference_in_calendar_weeks';
import differenceInYears from 'date-fns/difference_in_years';
import { differenceInDays } from 'date-fns';

export default {
    data() {
        return {
            data: [],
            currentViewIndex: 0,
            dataViews: [
                {
                    label: 'Gross',
                    potentialLabel: 'Potential Gross',
                    filter: 'money',
                    view: 'gross',
                },
                {
                    label: 'Attendance',
                    potentialLabel: 'Capacity',
                    view: 'attendance',
                    filter: 'wholeCommaNumber',
                },
            ],
        };
    },
    computed: {
        chartData() {
            if (!this.data.length) {
                return [];
            }

            const newData = [];

            this.data.forEach((d, i) => {
                newData.push(d);

                if (typeof this.data[i + 1] === 'undefined') {
                    // console.log('reached the end, stopping');
                    return;
                }

                let date = addDays(d.end, 7);

                while (differenceInDays(date, this.data[i + 1].end) !== 0) {
                    newData.push({
                        end: date,
                    });

                    date = addDays(date, 7);
                }
            });

            return newData.map(d => ({
                x: this.getXValues(
                    d.number ? d : undefined,
                    d.number ? d.number : undefined,
                ),
                y: Vue.filter('date')(d.end),
            }));
        },
        annotation() {
            const annotations = [];

            annotations.push(
                this.getAnnotationConfig({
                    label: 'COVID-19 Shutdown',
                    date: 'March 8, 2020',
                    order: 1,
                }),
            );

            annotations.push(
                this.getAnnotationConfig({
                    label: 'Re-opening',
                    date: 'August 8, 2021',
                    order: 2,
                }),
            );

            return {
                annotations,
            };
        },
        plugins() {
            return [
                {
                    beforeInit: chart => {
                        chart.data.datasets = chart.data.datasets.map(set => {
                            const lastNumberIndex = parseInt(
                                findLastKey(set.data, number => !isNaN(number)),
                                10,
                            );

                            set.pointRadius = set.data.map((item, index) =>
                                index === lastNumberIndex ? 3 : 0,
                            );

                            return set;
                        });
                    },
                },
                {
                    beforeInit: chart => {
                        chart.config.options.tooltips.callbacks = {
                            beforeLabel: (tooltipItem, data) => {
                                const config = this.chartData[tooltipItem.index]
                                    .x[tooltipItem.datasetIndex];

                                const br =
                                    tooltipItem.datasetIndex > 0 ? '\n' : '';

                                return br + config.label + ':';
                            },
                            label: (tooltipItem, data) => {
                                const config = this.chartData[tooltipItem.index]
                                    .x[tooltipItem.datasetIndex];

                                if (config.filter) {
                                    return Vue.filter(config.filter)(
                                        tooltipItem.yLabel,
                                    );
                                }

                                return tooltipItem.yLabel;
                            },
                            title: (tooltipItem, data) => {
                                const config = this.chartData[
                                    tooltipItem[0].index
                                ].x[0];

                                return `${tooltipItem[0].xLabel} (#${config.number})`;
                            },
                        };
                    },
                },
                {
                    beforeInit: chart => {
                        chart.config.options.scales.yAxes[0].ticks.callback = (
                            value,
                            index,
                            values,
                        ) => {
                            return shortenNumber(
                                value,
                                this.currentView.view === 'gross' ? '$' : '',
                            );
                        };
                    },
                },
            ];
        },
        currentView() {
            return this.dataViews[this.currentViewIndex];
        },
        upToDate() {
            return window.broadwayBusiness.upToDate;
        },
    },
    methods: {
        getXValues(existing, number) {
            const data = [
                {
                    label: this.currentView.label,
                    value: NaN,
                    filter: this.currentView.filter,
                    number,
                },
                {
                    label: this.currentView.potentialLabel,
                    value: NaN,
                    filter: this.currentView.filter,
                    number,
                },
            ];

            if (!existing) {
                return data;
            }

            data[0].value = existing[this.currentView.view].total;
            data[1].value =
                (data[0].value * 100) /
                (existing[this.currentView.view].potential / 100);

            if (this.currentView.view === 'gross') {
                data[0].value = data[0].value / 100;
                data[1].value = data[1].value / 100;
            }

            return data;
        },
        getAnnotationConfig({ label, date, order }) {
            return {
                drawTime: 'afterDatasetsDraw',
                type: 'line',
                mode: 'vertical',
                scaleID: 'x-axis-0',
                value: formatDate(date, 'M/D/YYYY'),
                borderColor: 'rgba(255, 255, 255, .5)',
                borderWidth: 1,
                borderDash: [4, 2],
                label: {
                    backgroundColor: 'rgba(255, 255, 255, 0)',
                    fontColor: 'rgba(255, 255, 255, .5)',
                    content: label,
                    enabled: true,
                    xAdjust: this.annotationXAdjust(label, date),
                    yAdjust: 10 + (order - 1) * 20,
                    position: 'top',
                    cornerRadius: 0,
                    xPadding: 0,
                    yPadding: 0,
                },
            };
        },
        annotationXAdjust(label, date) {
            const width = this.getTextWidth(label);

            const dateIndex = findIndex(this.chartData, data =>
                isEqual(data.y, date),
            );

            const adjustment = width / 2 + 5;

            if (dateIndex === -1) {
                return adjustment;
            }

            return dateIndex / this.chartData.length >= 0.8
                ? adjustment
                : -adjustment;
        },
        getTextWidth(label) {
            if (!this.textCtx) {
                const canvas = document.getElementById(
                    'canvas-text-width-helper',
                );
                this.textCtx = canvas.getContext('2d');
                this.textCtx.font = 'bold 12px sans-serif';
            }

            return this.textCtx.measureText(label).width;
        },
    },
    created() {
        api.get('week/stats/chart', {
            params: {
                uptodate: window.broadwayBusiness.upToDate ? 1 : 0,
            },
        }).then(response => (this.data = response.data));
    },
};
</script>
